import type { CalendarEntry, CalendarVersion, Content, RecordBase, MetricResult } from './model';
import type { PackageDestination } from './distribution';
import type { ManualMetricReading } from './manual-metrics';
import type { CtaException, CtaReview } from './package-review';

export type Role = 'admin' | 'editor' | 'approver' | 'reader';
export interface TeamMember extends RecordBase {
  name: string;
  role: Role;
  active: boolean;
  authEpoch?: number;
}
export interface Schedule extends RecordBase {
  contentId: string;
  date: string;
  proposalId: string;
  approvedBy: string;
  archiveDate: string;
  archiveHash: string | null;
}
export interface ProductionTask extends RecordBase {
  campaignId: string;
  contentId: string | null;
  title: string;
  owner: string;
  dueDate: string;
  status: 'pendente' | 'em_andamento' | 'concluida' | 'cancelada';
  evidence: string;
}
export interface Material extends RecordBase {
  campaignId: string;
  contentId: string;
  title: string;
  kind: 'roteiro' | 'legenda' | 'midia';
  text: string;
  url: string;
  fileName: string;
  mime: string;
  size: number;
  sha256: string;
  replacesId: string | null;
}
export interface PublicationPackage extends RecordBase {
  campaignId: string;
  contentId: string;
  revision: number;
  caption: string;
  destinations: string[];
  structuredDestinations?: PackageDestination[];
  plannedTime?: string;
  plannedTimezone?: 'America/Sao_Paulo';
  materialIds: string[];
  cta: string;
  ctaException?: CtaException;
  ctaPolicyVersion?: 1;
  ctaReview?: CtaReview;
  date: string;
  editorialNote: string;
  contentFormat: string;
  hash: string;
  status: 'em_revisao' | 'aprovada' | 'ajustes' | 'recusada' | 'substituida';
  decisionNote: string;
  decidedBy: string;
  decidedAt: string;
}
export interface BusinessEvent extends RecordBase {
  campaignId: string;
  contentId: string | null;
  kind: 'pedido' | 'qualificada' | 'conversa' | 'demonstracao' | 'oportunidade';
  contact: string;
  channel: string;
  date: string;
  evidence: string;
  rooms: number | null;
  customer: 'sim' | 'nao' | 'desconhecido';
  linkSent: boolean;
  contactPermission: boolean;
  status: 'valido' | 'anulado';
}
export interface CampaignGoal extends RecordBase {
  campaignId: string;
  metric: BusinessEvent['kind'] | 'publicacoes';
  target: number;
}
export interface CollectionPlan extends RecordBase {
  enabled: boolean;
  lastAttempt?: string;
  lastSuccess?: string;
  lastError?: string;
  slots: string[];
}
export interface CampaignResults {
  campaignId: string;
  pieces: number;
  publishedPieces: number;
  publications: number;
  events: Record<BusinessEvent['kind'], number>;
  goals: (CampaignGoal & { actual: number })[];
  media: {
    id: string;
    contentId: string;
    account: string;
    platform: string;
    url: string;
    source: 'manual' | 'meta' | 'api';
    precision: 'day' | 'second';
    publishedAt: string;
    collectedAt: string | null;
    latestReadingId?: string;
    metrics: MetricResult[];
    readings?: CampaignReading[];
    manualReadings?: ManualMetricReading[];
    archiveReadings?: import('./archive-history').ArchiveMetricReading[];
    archivePublicationEvidence?: import('./archive-history').ArchivePublicationEvidence[];
    provenance?: 'archive';
    latestManualReadingId?: string;
  }[];
  interactions: number | null;
  measuredPublications: number;
  manualMeasuredPublications?: number;
  interactionCoverage: number;
  generatedAt: string;
}
export interface CampaignReading {
  id: string;
  kind: 'lifetime_snapshot';
  source: 'meta';
  apiVersion: string;
  collectedAt: string;
  windowStart: string;
  windowEnd: string;
  timezone: 'UTC';
  ageSeconds: number;
  collectionKind?: 'slot' | 'd7';
  collectionDueAt?: string;
  delaySeconds?: number;
  metrics: MetricResult[];
}
export interface CampaignOperations {
  tasks: ProductionTask[];
  materials: Material[];
  packages: PublicationPackage[];
  events: BusinessEvent[];
  goals: CampaignGoal[];
  results: CampaignResults;
}
export function operationalCalendar(data: {
  calendars: CalendarVersion[];
  contents: Content[];
  schedules?: Schedule[];
}): CalendarEntry[] {
  const entries = new Map(
    (data.calendars.find((c) => c.kind === 'vigente')?.entries ?? []).map((e) => [
      e.contentId,
      { ...e },
    ]),
  );
  for (const schedule of data.schedules ?? []) {
    const content = data.contents.find((c) => c.id === schedule.contentId);
    if (content && content.stage !== 'descartado')
      entries.set(content.id, {
        contentId: content.id,
        title: content.title,
        format: content.format,
        date: schedule.date,
        stage: content.stage,
        reserve: content.reserve,
      });
  }
  return [...entries.values()];
}
